import { cn } from '@repo/ui/lib/cn';
import { CalendarHeart, Heart, LogIn } from 'lucide-react';

import { landingContent } from '@/constants/landing-content';

const { howItWorks } = landingContent;

const iconMap = {
  CalendarHeart,
  LogIn,
  Heart,
} as const;

interface StepCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: readonly string[];
}

function StepCard({ icon, title, description, benefits }: StepCardProps) {
  return (
    <div
      className={cn(
        'bg-card text-card-foreground relative rounded-2xl border p-6 transition-all duration-300 ease-in-out',
        'hover:border-primary/50 hover:bg-muted hover:shadow-lg',
      )}
    >
      <div className="bg-muted text-primary mb-4 flex h-12 w-12 items-center justify-center rounded-lg">
        {icon}
      </div>
      <h3 className="mb-2 text-xl font-semibold">{title}</h3>
      <p className="text-muted-foreground mb-6">{description}</p>
      <ul className="space-y-3">
        {benefits.map((benefit, index) => (
          <li key={index} className="flex items-center gap-3">
            <div className="bg-primary/20 flex h-4 w-4 shrink-0 items-center justify-center rounded-full">
              <div className="bg-primary h-2 w-2 rounded-full" />
            </div>
            <span className="text-muted-foreground text-sm">{benefit}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HowItWorksSection() {
  return (
    <section id={howItWorks.id} className="bg-background w-full py-16 sm:py-24">
      <div className="container mx-auto max-w-6xl px-4">
        {/* Section Header */}
        <div className="mx-auto mb-16 max-w-4xl text-center">
          <h2 className="text-foreground text-4xl font-bold tracking-tight sm:text-5xl">
            {howItWorks.headline}
          </h2>
          <p className="text-muted-foreground mt-4 text-lg">
            {howItWorks.subheadline}
          </p>
        </div>

        {/* Step Indicators with Connecting Line */}
        <div className="relative mx-auto mb-8 w-full max-w-4xl">
          <div
            aria-hidden="true"
            className="bg-border absolute top-1/2 left-[16.6667%] h-0.5 w-[66.6667%] -translate-y-1/2"
          />
          <div className="relative grid grid-cols-3">
            {howItWorks.steps.map((_, index) => (
              <div
                key={index}
                className="bg-muted text-foreground ring-background flex h-8 w-8 items-center justify-center justify-self-center rounded-full font-semibold ring-4"
              >
                {index + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Steps Grid */}
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3">
          {howItWorks.steps.map((step, index) => {
            const Icon = iconMap[step.icon as keyof typeof iconMap];
            return (
              <StepCard
                key={index}
                icon={<Icon className="h-6 w-6" />}
                title={step.title}
                description={step.description}
                benefits={step.benefits}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
